import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { ZincCatalog, ZincProduct } from "@t3/shared";
import { useParams, useSearchParams } from "react-router-dom";
import { useFetchProductCatalog } from "@/api/fetchProductCatalog";

interface ProductCatalogContextType {
  catalog?: ZincCatalog["results"];
  loading: boolean;
  error: string | null;
  search?: string | null;
  loadMoreProducts: () => Promise<ZincCatalog | undefined>;
  hasMoreProducts: boolean;
  selectedProduct: ZincProduct | null;
  setSearch: (search: string) => void;
}

const ProductCatalogContext = createContext<ProductCatalogContextType>({
  catalog: undefined,
  loading: false,
  error: null,
  loadMoreProducts: async () => undefined,
  hasMoreProducts: false,
  selectedProduct: null,
  setSearch: () => {},
});

export const useProductCatalogContext = () => {
  return useContext(ProductCatalogContext);
};

export const ProductCatalogProvider = (props: any) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const search = searchParams.get("search");
  const { productId: selectedProductId } = useParams();
  const pagesRef = useRef<{ [x: number]: ZincCatalog }>({});
  const [itemsCache, setItemsCache] = useState<Record<string, ZincProduct>>({});
  const [loading, setLoading] = useState(false);
  const [fetchProductCatalog, _result, _loading, error] =
    useFetchProductCatalog();

  const getNextPage = () => {
    let lastPage = 0;
    Object.keys(pagesRef.current).map((key) => {
      if (parseInt(key) > lastPage) {
        lastPage = parseInt(key);
      }
    });
    return lastPage + 1;
  };
  const hasMoreProducts =
    !pagesRef.current[getNextPage() - 1] ||
    pagesRef.current[getNextPage() - 1]?.results.length > 0;

  const fetchData = useCallback(
    async (
      opts: {
        productId?: string;
        newSearch?: boolean;
        isCancelled?: boolean;
      } = {},
    ) => {
      const { productId } = opts;
      const page = getNextPage();
      const searchTerm = search || "travel gifts";
      setLoading(true);
      const res = await fetchProductCatalog(
        productId
          ? { productId }
          : {
              search: searchTerm,
              page,
            },
      );
      const { newSearch, isCancelled } = opts;
      if (isCancelled) {
        return;
      }
      setLoading(false);
      if (newSearch) {
        pagesRef.current = {};
      }
      if (!productId) {
        pagesRef.current = { ...pagesRef.current, [page]: res };
      }
      res.results.forEach((item) => {
        let insert = item;
        if (insert.product_details?.length === 0) {
          delete insert.product_details; // catalog always returns empty, so we need to delete it to not overwrite the detail api call
        }
        setItemsCache((itemsCache) => ({
          ...itemsCache,
          [item.product_id]: { ...itemsCache[item.product_id], ...insert },
        }));
      });
      return res;
    },
    [search, fetchProductCatalog],
  );

  useEffect(() => {
    pagesRef.current = {};
    let opts: {
      productId?: string;
      newSearch?: boolean;
      isCancelled?: boolean;
    } = {
      newSearch: true,
      isCancelled: false,
    };
    fetchData(opts);
    return () => {
      opts.isCancelled = true;
    };
  }, [search]);

  useEffect(() => {
    if (selectedProductId) {
      fetchData({ productId: selectedProductId });
    }
  }, [fetchData, selectedProductId]);

  const catalog = useMemo(() => {
    return Object.values(pagesRef.current).reduce(
      (acc, page) => {
        return [...acc, ...page.results];
      },
      [] as ZincCatalog["results"],
    );
  }, [getNextPage(), loading]);

  const selectedProduct = useMemo(() => {
    return selectedProductId ? itemsCache[selectedProductId] : null;
  }, [itemsCache, selectedProductId]);

  const setSearch = useCallback(
    (search: string) => {
      setSearchParams({ search });
    },
    [setSearchParams],
  );

  const contextValue: ProductCatalogContextType = {
    catalog,
    loading,
    error,
    search,
    loadMoreProducts: fetchData,
    hasMoreProducts,
    selectedProduct,
    setSearch,
  };

  return <ProductCatalogContext.Provider {...props} value={contextValue} />;
};
